import { CompanyContext } from 'contexts/CompanyContextProvider';
import React, { useContext, useEffect, useState } from 'react';
import { RegionModel, FileLoadingModel } from 'services/EpdClient';
import EPDService from 'services/EpdService';
import {
  addEpdFile,
  useAddCoverPageFile,
  useAddOrganizationInformationFile,
  useArrangeOrganizationInformationFiles,
  useRemoveCoverPageFile,
  useRemoveOrganizationInformationFile,
  useUpdateEpdOrganizationInformation,
  useUpdateEpdOrganizationInformationAddress,
} from 'services/api/mutations';
import {
  useCompanyUsersLookup,
  useCountriesLookup,
  useEpdCoverPageInformation,
  useEpdOrganizationInformation,
  useEpdOrganizationInformationAddress,
  useRegionsLookup,
} from 'services/api/queries';
import { FieldPanel, FieldPanelFullWidth, FieldPanelHeader, HelpBoxContainer } from 'styles/v2/Styles.styled';
import { Option, UserOptionModel } from 'types/types';
import { getValidationIssueMessage } from 'util/compilerValidation';

import CompilerSelectControlledComponent from '../epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextAreaComponent from '../epd-wizard-inputs/WizardTextArea';
import WizardTextInputComponent from '../epd-wizard-inputs/WizardTextInput';
import WizardTextInputControlledComponent from '../epd-wizard-inputs/WizardTextInputControlled';
import WizardTextInputWithAutoComplete from '../epd-wizard-inputs/WizardTextInputWithAutoComplete';
import { HelpBox } from '../help-boxes';
import TooltipErrorIcon from '../icons/TooltipErrorIcon';
import { ImagesUpload } from '../images-upload';
import SmartLabel from '../labels/SmartLabel';
import { ErrorAlertPanel } from './panels/Styles.styled';
import { usePcrV2Loading } from './hooks/usePcrV2';
import SkeletonOrganizationTab from './skeletons/SkeletonOrganizationTab';

const OrganizationTab: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  validationState: any;
  canUpload: boolean;
  isReadOnly: boolean;
  fileLoading?: FileLoadingModel[];
}> = ({ epdId, epdVersionId, validationState }) => {
  const { companyId } = useContext(CompanyContext);

  const countries = useCountriesLookup().data;
  const epdOrganizationAddress = useEpdOrganizationInformationAddress(epdVersionId).data;
  const organizationInformation = useEpdOrganizationInformation(epdVersionId).data;
  const isPcrV2Loading = usePcrV2Loading();
  const regions = useRegionsLookup().data;

  const coverPageInformation = useEpdCoverPageInformation(epdVersionId).data;
  const addCoverPageFileMutation = useAddCoverPageFile(epdVersionId);
  const deleteCoverPageFileMutation = useRemoveCoverPageFile(epdVersionId);

  const contactUsersLookup = useCompanyUsersLookup(companyId as string).data;
  const updateMutation = useUpdateEpdOrganizationInformation(epdVersionId);
  const updateAddressMutation = useUpdateEpdOrganizationInformationAddress(epdVersionId);
  const addOrganizationInformationFileMutation = useAddOrganizationInformationFile(epdVersionId);
  const removeOrganizationInformationFileMutation = useRemoveOrganizationInformationFile(epdVersionId);
  const arrangeOrganizationInformationFilesMutation = useArrangeOrganizationInformationFiles(epdVersionId);

  const [suggestedUsers, setSuggestedUsers] = useState<string[] | undefined>([]);
  const [selectedContactPersonName, setSelectedContactPersonName] = useState<string | undefined>(
    organizationInformation?.contactPersonName
  );

  useEffect(() => {
    setSelectedContactPersonName(organizationInformation?.contactPersonName);
  }, [organizationInformation?.contactPersonName]);

  const isLoading =
    countries === undefined ||
    epdOrganizationAddress === undefined ||
    organizationInformation === undefined ||
    coverPageInformation === undefined ||
    isPcrV2Loading ||
    validationState === undefined ||
    regions === undefined;
  if (isLoading) {
    return <SkeletonOrganizationTab />;
  }

  const onAddCoverPageFile = async (propertyName: string, file: File) => {
    await addEpdFile(
      { epdId: epdId, propertyName, file, documentTypeId: null, documentLanguageId: null },
      addCoverPageFileMutation
    );
  };

  const onChangeEpdContactPersonEmail = async (propertyName: string, value: string | null) => {
    const contactUserName = contactUsersLookup?.find(
      (user: UserOptionModel) => user.label.toLowerCase() === (value ?? '').toLowerCase()
    )?.userName;

    if (contactUserName && contactUserName !== selectedContactPersonName) {
      setSelectedContactPersonName(contactUserName);
    }

    if (value !== organizationInformation.contactPersonEmail) {
      onChangeEpdOrganizationInformation(propertyName, value);
    }
  };

  const onSelectSuggestedEpdContactPersonEmail = async (value: any) => {
    const contactUserName = contactUsersLookup?.find(
      (user: UserOptionModel) => user.label.toLowerCase() === value.toLowerCase()
    )?.userName;

    if (contactUserName && contactUserName !== selectedContactPersonName) {
      setSelectedContactPersonName(contactUserName);
    }
  };

  const onChangeEpdOrganizationInformation = async (propertyName: string, value: any) => {
    updateMutation.mutate({ epdId: epdId, versionId: epdVersionId, propertyName: propertyName, newValue: value });
  };

  const onAddOrganizationInformationFile = async (propertyName: string, file: File) => {
    await addEpdFile(
      { epdId: epdId, propertyName, file, documentTypeId: null, documentLanguageId: null },
      addOrganizationInformationFileMutation
    );
  };

  const onRemoveOrganizationInformationFile = async (fileId: string, propertyName: string) => {
    removeOrganizationInformationFileMutation.mutate(fileId);
  };

  const onRemoveCoverPageFile = async (fileId: string, propertyName: string) => {
    deleteCoverPageFileMutation.mutate(fileId);
  };

  const onChangeEpdOrganizationInformationAddress = async (propertyName: string, val: any) => {
    updateAddressMutation.mutate({ epdId: epdId, versionId: epdVersionId, propertyName: propertyName, newValue: val });
  };

  const onChangeCountry = (propName: string, value: any) => {
    if (Array.isArray(countries)) {
      onChangeEpdOrganizationInformationAddress(propName, value);
    } else {
      onChangeEpdOrganizationInformationAddress(propName, undefined);
    }
    onChangeEpdOrganizationInformationAddress('regionId', undefined);
  };

  const selectedCountryOrginal =
    countries.find((country: any) => country.id === epdOrganizationAddress.address.countryId) ?? null;

  const selectedCountry = epdOrganizationAddress.address.countryId
    ? ({
        value: selectedCountryOrginal?.id,
        label: selectedCountryOrginal?.name,
      } as Option)
    : null;

  const availableRegionsForCountry = regions.filter((region) => region.countryId === selectedCountry?.value);
  const addressRegion = regions.find((region: any) => region.id === epdOrganizationAddress.address.regionId);
  const selectedRegion = addressRegion
    ? ({
        value: addressRegion.id,
        label: addressRegion.name,
      } as Option)
    : null;

  const searchDevelopersEmail = (event: any) => {
    setSuggestedUsers(
      contactUsersLookup
        ?.filter((user: UserOptionModel) => user.label.toLowerCase().startsWith(event.query.toLowerCase()))
        .map((filteredDev: UserOptionModel) => filteredDev.label)
    );
  };

  return (
    <>
      <FieldPanelFullWidth>
        <FieldPanelHeader>Contact information</FieldPanelHeader>
        <WizardTextInputComponent
          label="EPD Owner"
          tooltip="Organisation that is the sole owner and has the liability and responsibility for the EPD."
          name="companyName"
          disabled
          value={organizationInformation?.companyName}
        />
        <WizardTextInputWithAutoComplete
          label="Contact person e-mail"
          tooltip="Please select which team member should be the main contact for EPD queries."
          value={organizationInformation.contactPersonEmail}
          onChanged={onChangeEpdContactPersonEmail}
          onSelectSuggested={onSelectSuggestedEpdContactPersonEmail}
          required
          placeholder="Type here"
          name="contactPersonEmail"
          suggestions={suggestedUsers}
          completeMethod={searchDevelopersEmail}
          error={validationState.errors.organizationInformation?.contactPersonEmail}
        />
        <WizardTextInputControlledComponent
          label="Contact person name"
          tooltip="Person name who should be the main contact for EPD queries."
          onChanged={onChangeEpdOrganizationInformation}
          onChange={(e: any) => setSelectedContactPersonName(e.target.value)}
          required
          placeholder="Name and last name"
          name="contactPersonName"
          value={selectedContactPersonName ?? undefined}
          error={validationState.errors.organizationInformation?.contactPersonName}
        />
      </FieldPanelFullWidth>
      <FieldPanel>
        <FieldPanelHeader className="w-full">
          Organisation address
          {!!validationState.errors.organizationInformationAddress && (
            <TooltipErrorIcon content={validationState.errors.organizationInformationAddress} />
          )}
        </FieldPanelHeader>

        {validationState.errors.organizationInformationAddress?.countryDiffersFromCompany && (
          <ErrorAlertPanel className="w-full">
            {getValidationIssueMessage(validationState.errors.organizationInformationAddress.countryDiffersFromCompany)}
          </ErrorAlertPanel>
        )}

        <CompilerSelectControlledComponent
          label="Country"
          name="countryId"
          options={countries.map((country: any) => {
            return {
              value: country?.id,
              label: country?.name,
            } as Option;
          })}
          required
          value={selectedCountry}
          onChanged={onChangeCountry}
          placeholder="Select"
          error={
            validationState.errors.organizationInformationAddress?.country ||
            validationState.errors.organizationInformationAddress?.countryDiffersFromCompany
          }
        />

        {availableRegionsForCountry.length > 0 && (
          <CompilerSelectControlledComponent
            label="Region"
            name="regionId"
            options={availableRegionsForCountry.map((region: RegionModel) => {
              return {
                value: region?.id,
                label: region?.name,
              } as Option;
            })}
            value={selectedRegion}
            onChanged={onChangeEpdOrganizationInformationAddress}
            placeholder="Select"
            error={validationState.errors.organizationInformationAddress?.region}
          />
        )}

        <WizardTextInputComponent
          label="ZIP code"
          tooltipPosition={'right'}
          required
          placeholder="Type here"
          value={epdOrganizationAddress.address.zipCode}
          onChanged={onChangeEpdOrganizationInformationAddress}
          name="zipCode"
          error={validationState.errors.organizationInformationAddress?.zipCode}
        />
        <WizardTextInputComponent
          label="City"
          required
          placeholder="Type here"
          value={epdOrganizationAddress.address.city}
          onChanged={onChangeEpdOrganizationInformationAddress}
          name="city"
          error={validationState.errors.organizationInformationAddress?.city}
        />
        <WizardTextInputComponent
          label="Address line 1"
          required
          placeholder="Type here"
          value={epdOrganizationAddress.address.addressLine1}
          onChanged={onChangeEpdOrganizationInformationAddress}
          name="addressLine1"
          error={validationState.errors.organizationInformationAddress?.addressLine}
        />
        <WizardTextInputComponent
          label="Address line 2"
          placeholder="Type here"
          value={epdOrganizationAddress.address.addressLine2}
          onChanged={onChangeEpdOrganizationInformationAddress}
          name="addressLine2"
        />
      </FieldPanel>
      <FieldPanelFullWidth>
        <FieldPanelHeader>Organisation details</FieldPanelHeader>

        <WizardTextAreaComponent
          label="Description of the organisation"
          placeholder="Type here"
          tooltip="This may include information on products - or management system-related certifications (e.g. ISO 14024 Type I environmental labels, ISO 9001- and 14001-certificates and EMAS-registrations) and other relevant work the organisation wants to communicate (e.g. SA 8000, supply-chain management and social responsibility)."
          name="description"
          value={organizationInformation.description}
          onChanged={onChangeEpdOrganizationInformation}
          error={validationState.errors.organizationInformation?.description}
          required
        />
        <div>
          <SmartLabel
            label={'Organisation logo'}
            required
            hasValue={coverPageInformation.companyLogoImages && coverPageInformation.companyLogoImages.length > 0}
            error={validationState.errors.companyLogoImages}
          />
          <ImagesUpload
            name={'CompanyLogoImages'}
            single
            images={coverPageInformation.companyLogoImages}
            onUpload={onAddCoverPageFile}
            onReorder={(imgIds: string[]) => EPDService.arrangeEpdFiles(imgIds)}
            onRemove={onRemoveCoverPageFile}
          />
        </div>

        <div>
          <SmartLabel
            label={'Organisation images'}
            tooltip="Images which help to describe and identify your organisation."
          />
          <ImagesUpload
            name={'OrganizationImages'}
            images={organizationInformation.organizationImages}
            onUpload={onAddOrganizationInformationFile}
            onReorder={(imgIds: string[]) => arrangeOrganizationInformationFilesMutation.mutate(imgIds)}
            onRemove={onRemoveOrganizationInformationFile}
          />
          <HelpBoxContainer>
            {organizationInformation?.organizationImages && organizationInformation?.organizationImages.length > 1 && (
              <HelpBox>
                <span>Use drag and drop to reorder the images.</span>
              </HelpBox>
            )}
            <HelpBox>
              <span>
                Upload an image with a recommended resolution of 150–300 DPI. Images will be proportionally adjusted to fit
                the page size. To see image placement in the document, use the "PDF preview" option in the upper right
                corner.
              </span>
            </HelpBox>
          </HelpBoxContainer>
        </div>
      </FieldPanelFullWidth>
    </>
  );
};

export default OrganizationTab;
